import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Spin, Switch, Alert } from 'antd';
import { useTranslation } from 'react-i18next';

import { BasicFormWrapper } from 'container/styled';
import { Modal } from 'components/modals/antd-modals';
import openNotificationWithIcon from 'utility/notification';
import { CollectionChannelConfig } from 'constants/constants';
import {
  debtCollectionApi,
  DebtCollectionChannelConfigDto,
  CollectionChannel,
} from '../debtCollectionApi';
import { EmailOptionsApi, EmailOptionsDto } from 'api';

interface ChannelConfigModalProps {
  visible: boolean;
  channel: CollectionChannel;
  config: DebtCollectionChannelConfigDto | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const CREDENTIAL_FIELDS: Partial<
  Record<CollectionChannel, { key: string; label: string; type?: string }[]>
> = {
  [CollectionChannel.Sms]: [
    { key: 'AccountSid', label: 'Twilio Account SID' },
    { key: 'AuthToken', label: 'Twilio Auth Token', type: 'password' },
    { key: 'FromNumber', label: 'From Phone Number' },
  ],
  [CollectionChannel.WhatsApp]: [
    { key: 'AccountSid', label: 'Twilio Account SID' },
    { key: 'AuthToken', label: 'Twilio Auth Token', type: 'password' },
    { key: 'FromWhatsAppNumber', label: 'From WhatsApp Number' },
  ],
  [CollectionChannel.Telegram]: [
    { key: 'BotToken', label: 'Telegram Bot Token', type: 'password' },
  ],
  [CollectionChannel.Webhook]: [
    { key: 'Headers', label: 'Auth Headers (JSON)' },
  ],
};

const ChannelConfigModal: React.FC<ChannelConfigModalProps> = ({
  visible,
  channel,
  config,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [emailOptions, setEmailOptions] = useState<EmailOptionsDto | null>(null);
  const [loadingEmailOptions, setLoadingEmailOptions] = useState(false);

  const channelCfg = CollectionChannelConfig[channel];
  const fields = CREDENTIAL_FIELDS[channel];
  const isExternalChannel = fields != null;
  const isEmailChannel = channel === CollectionChannel.Email;

  useEffect(() => {
    form.resetFields();
    setTestResult(null);
    if (config) {
      form.setFieldsValue({ isEnabled: config.isEnabled ?? false });
    }

    // Fetch email options when opening email channel config
    if (visible && isEmailChannel) {
      const fetchEmailOptions = async () => {
        setLoadingEmailOptions(true);
        try {
          const emailApi = new EmailOptionsApi();
          const { data } = await emailApi.emailOptionsActiveGet();
          setEmailOptions(data);
        } catch (error) {
          console.error('Failed to fetch email options:', error);
          setEmailOptions(null);
        } finally {
          setLoadingEmailOptions(false);
        }
      };
      fetchEmailOptions();
    }
  }, [config, visible, isEmailChannel, form]);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await debtCollectionApi.testChannel(channel);
      setTestResult(data);
    } catch {
      setTestResult({ success: false, message: 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (values: Record<string, boolean | string>) => {
    setSaving(true);
    try {
      let credentialsJson: string | undefined;

      if (isExternalChannel) {
        // External channels: collect credentials from form fields
        credentialsJson = JSON.stringify(
          fields!.reduce((acc: Record<string, string>, f) => {
            acc[f.key] = (values[f.key] as string) || '';
            return acc;
          }, {}),
        );
      } else if (isEmailChannel && emailOptions) {
        // Email channel: use organization SMTP settings
        credentialsJson = JSON.stringify({
          From: emailOptions.from,
          SmtpHost: emailOptions.smtpHost,
          SmtpPort: emailOptions.smtpPort,
          SmtpUser: emailOptions.smtpUser,
          SmtpPass: emailOptions.smtpPass,
        });
      }

      await debtCollectionApi.upsertChannel(channel, {
        isEnabled: values.isEnabled as boolean,
        credentialsJson,
      });
      openNotificationWithIcon(
        'success',
        t('debt-collection:channels.saved', 'Channel configuration saved'),
      );
      onSuccess();
    } catch (error) {
      console.error('Error saving channel:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      type="primary"
      title={`${t('debt-collection:channels.configure', 'Configure')} ${channelCfg.title}`}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={550}
    >
      <BasicFormWrapper>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label={t('debt-collection:channels.enabled', 'Enabled')}
            name="isEnabled"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {isExternalChannel ? (
            fields!.map((f) => (
              <Form.Item
                key={f.key}
                label={f.label}
                name={f.key}
                rules={[{ required: true, message: `${f.label} is required` }]}
              >
                <Input type={f.type || 'text'} placeholder={f.label} />
              </Form.Item>
            ))
          ) : isEmailChannel ? (
            <div style={{ marginBottom: 16 }}>
              {loadingEmailOptions ? (
                <Spin />
              ) : emailOptions ? (
                <>
                  <Alert
                    message={t('debt-collection:channels.uses-smtp', 'Uses the organization SMTP configuration.')}
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                  />
                  <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, fontSize: 12 }}>
                    <div><strong>From:</strong> {emailOptions.from}</div>
                    <div><strong>SMTP Host:</strong> {emailOptions.smtpHost}</div>
                    <div><strong>SMTP Port:</strong> {emailOptions.smtpPort}</div>
                    <div><strong>SMTP User:</strong> {emailOptions.smtpUser}</div>
                    <div><strong>Password:</strong> ••••••••</div>
                  </div>
                </>
              ) : (
                <Alert
                  message={t('debt-collection:channels.no-smtp', 'No SMTP configuration found. Please configure email settings first.')}
                  type="warning"
                  showIcon
                />
              )}
            </div>
          ) : (
            <p style={{ color: '#888', marginBottom: 16 }}>
              {channel === CollectionChannel.Push
                ? t('debt-collection:channels.uses-firebase', 'Uses the existing Firebase push notification setup.')
                : t('debt-collection:channels.uses-internal', 'Uses the internal notification system.')}
            </p>
          )}

          {testResult && (
            <div style={{ marginBottom: 16, color: testResult.success ? '#52c41a' : '#ff4d4f' }}>
              {testResult.message}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {isExternalChannel && (
              <Button onClick={handleTestConnection} loading={testing}>
                {t('debt-collection:channels.test', 'Test Connection')}
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              disabled={isEmailChannel && !emailOptions}
            >
              {t('global:save', 'Save')}
            </Button>
          </div>
        </Form>
      </BasicFormWrapper>
    </Modal>
  );
};

export default ChannelConfigModal;
